import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DollarSignIcon, TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { formatCurrency } from '../../utils/currencyFormatter';
export function BudgetVsActualReport({
  filters,
  locale
}) {
  // Sample data for budget vs actual report
  const budgetData = [{
    department: 'Marketing',
    budget: 150000,
    actual: 142500,
    variance: -7500
  }, {
    department: 'Sales',
    budget: 200000,
    actual: 215000,
    variance: 15000
  }, {
    department: 'IT',
    budget: 300000,
    actual: 285000,
    variance: -15000
  }, {
    department: 'Operations',
    budget: 250000,
    actual: 248000,
    variance: -2000
  }, {
    department: 'HR',
    budget: 100000,
    actual: 98500,
    variance: -1500
  }, {
    department: 'Finance',
    budget: 120000,
    actual: 115000,
    variance: -5000
  }];
  const monthlyData = [{
    month: 'Jan',
    budget: 95000,
    actual: 92000
  }, {
    month: 'Feb',
    budget: 100000,
    actual: 98500
  }, {
    month: 'Mar',
    budget: 110000,
    actual: 115000
  }, {
    month: 'Apr',
    budget: 105000,
    actual: 107000
  }, {
    month: 'May',
    budget: 115000,
    actual: 110000
  }, {
    month: 'Jun',
    budget: 120000,
    actual: 118000
  }];
  const totalBudget = budgetData.reduce((sum, item) => sum + item.budget, 0);
  const totalActual = budgetData.reduce((sum, item) => sum + item.actual, 0);
  const totalVariance = totalActual - totalBudget;
  const variancePercentage = totalVariance / totalBudget * 100;
  return <div className="p-6" data-id="element-15732">
      <div className="mb-8" data-id="element-15733">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-15734">
          Budget vs Actual Spend Report
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-15735">
          Compare budgeted amounts against actual spend by department, GL code,
          or entity
        </p>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-15736">
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15737">
          <div className="p-5" data-id="element-15738">
            <div className="flex items-center" data-id="element-15739">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3" data-id="element-15740">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-15741" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15742">
                <dl data-id="element-15743">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15744">
                    Total Budget
                  </dt>
                  <dd data-id="element-15745">
                    <div className="text-lg font-medium text-gray-900" data-id="element-15746">
                      {formatCurrency(totalBudget, 'USD', locale)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15747">
          <div className="p-5" data-id="element-15748">
            <div className="flex items-center" data-id="element-15749">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3" data-id="element-15750">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-15751" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15752">
                <dl data-id="element-15753">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15754">
                    Total Actual
                  </dt>
                  <dd data-id="element-15755">
                    <div className="text-lg font-medium text-gray-900" data-id="element-15756">
                      {formatCurrency(totalActual, 'USD', locale)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-15757">
          <div className="p-5" data-id="element-15758">
            <div className="flex items-center" data-id="element-15759">
              <div className={`flex-shrink-0 ${totalVariance < 0 ? 'bg-green-500' : 'bg-red-500'} rounded-md p-3`} data-id="element-15760">
                {totalVariance < 0 ? <TrendingDownIcon className="h-6 w-6 text-white" data-id="element-15761" /> : <TrendingUpIcon className="h-6 w-6 text-white" data-id="element-15762" />}
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15763">
                <dl data-id="element-15764">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15765">
                    Variance
                  </dt>
                  <dd data-id="element-15766">
                    <div className="text-lg font-medium text-gray-900" data-id="element-15767">
                      {formatCurrency(totalVariance, 'USD', locale)} (
                      {variancePercentage.toFixed(1)}%)
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Department Chart */}
      <div className="bg-white p-6 rounded-lg shadow mb-8" data-id="element-15768">
        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-15769">
          Budget vs Actual by Department
        </h4>
        <div className="h-80" data-id="element-15770">
          <ResponsiveContainer width="100%" height="100%" data-id="element-15771">
            <BarChart data={budgetData} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-15772">
              <CartesianGrid strokeDasharray="3 3" data-id="element-15773" />
              <XAxis dataKey="department" data-id="element-15774" />
              <YAxis data-id="element-15775" />
              <Tooltip formatter={value => formatCurrency(value, 'USD', locale)} data-id="element-15776" />
              <Legend data-id="element-15777" />
              <Bar dataKey="budget" name="Budget" fill="#3b82f6" data-id="element-15778" />
              <Bar dataKey="actual" name="Actual" fill="#10b981" data-id="element-15779" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Monthly Trend */}
      <div className="bg-white p-6 rounded-lg shadow mb-8" data-id="element-15780">
        <h4 className="text-base font-medium text-gray-900 mb-4" data-id="element-15781">
          Monthly Budget vs Actual Trend
        </h4>
        <div className="h-80" data-id="element-15782">
          <ResponsiveContainer width="100%" height="100%" data-id="element-15783">
            <BarChart data={monthlyData} margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5
          }} data-id="element-15784">
              <CartesianGrid strokeDasharray="3 3" data-id="element-15785" />
              <XAxis dataKey="month" data-id="element-15786" />
              <YAxis data-id="element-15787" />
              <Tooltip formatter={value => formatCurrency(value, 'USD', locale)} data-id="element-15788" />
              <Legend data-id="element-15789" />
              <Bar dataKey="budget" name="Budget" fill="#3b82f6" data-id="element-15790" />
              <Bar dataKey="actual" name="Actual" fill="#10b981" data-id="element-15791" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Department Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-15792">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-15793">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-15794">
            Department Budget Details
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-15795">
            Detailed breakdown by department
          </p>
        </div>
        <div className="overflow-x-auto" data-id="element-15796">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-15797">
            <thead className="bg-gray-50" data-id="element-15798">
              <tr data-id="element-15799">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15800">
                  Department
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15801">
                  Budget
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15802">
                  Actual
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15803">
                  Variance
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15804">
                  % Variance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-15805">
              {budgetData.map((dept, index) => <tr key={index} data-id="element-15806">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-15807">
                    {dept.department}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-15808">
                    {formatCurrency(dept.budget, 'USD', locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right" data-id="element-15809">
                    {formatCurrency(dept.actual, 'USD', locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right" data-id="element-15810">
                    <span className={`${dept.variance < 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-15811">
                      {formatCurrency(dept.variance, 'USD', locale)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right" data-id="element-15812">
                    <span className={`${dept.variance < 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-15813">
                      {(dept.variance / dept.budget * 100).toFixed(1)}%
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}